import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'
import { environment } from 'src/environments/environment'
import { AwsService } from './aws.service'

export type ThumbnailResource = 'model' | 'project'
export interface ThumbnailPolicy {
 source: 'automatic' | 'user'
 revision: number
 generatedRevision: number | null
 generatedAt: string | null
 version: number
 thumbnail: string | null
}
type PreviewImage = { file: File; preview: string }
type PreviewView = { policy: ThumbnailPolicy | null; preview: string | null; status: 'idle' | 'generating' | 'error' }

@Injectable({ providedIn: 'root' })
export class ThumbnailPolicyService {
 private views = new Map<string, PreviewView>()
 private pending = new Map<string, Promise<string | undefined>>()
 private epochs = new Map<string, number>()
 constructor(private http: HttpClient, private aws: AwsService) {}
 private key(resource: ThumbnailResource, id: number) { return resource + ':' + id }
 private url(resource: ThumbnailResource, id: number) { return environment.api + '/' + resource + '/' + id + '/thumbnail-policy' }
 view(resource: ThumbnailResource, id: number): PreviewView {
  const key = this.key(resource,id)
  let view=this.views.get(key)
  if (!view) { view={policy:null,preview:null,status:'idle'}; this.views.set(key,view) }
  return view
 }
 async load(resource: ThumbnailResource, id: number) {
  const key=this.key(resource,id)
  const epoch=this.epochs.get(key)??0
  const policy = await firstValueFrom(this.http.get<ThumbnailPolicy>(this.url(resource,id)))
  const view=this.view(resource,id)
  if((this.epochs.get(key)??0)===epoch) { view.policy=policy; view.preview=policy.thumbnail }
  return policy
 }
 async useAutomatic(resource: ThumbnailResource, id: number) {
  const key=this.key(resource,id)
  const epoch=(this.epochs.get(key)??0)+1
  this.epochs.set(key,epoch)
  const policy=await this.load(resource,id)
  if(this.epochs.get(key)!==epoch) return
  await firstValueFrom(this.http.put(this.url(resource,id),{action:'automatic',version:policy.version}))
  if(this.epochs.get(key)===epoch) await this.load(resource,id)
 }
 async saveUserImage(resource: ThumbnailResource, id: number, image: PreviewImage) {
  const key=this.key(resource,id)
  const epoch=(this.epochs.get(key)??0)+1
  this.epochs.set(key,epoch)
  try {
   this.view(resource,id).status='generating'
   const policy=await this.load(resource,id)
   return await this.persist(resource,id,policy,'user',image,epoch)
  } catch(error) {
   if(this.epochs.get(key)===epoch && error instanceof HttpErrorResponse && error.status===409) {
    await this.load(resource,id).catch(()=>undefined)
   }
   if(this.epochs.get(key)===epoch) this.view(resource,id).status='error'
   return undefined
  }
 }
 generate(resource: ThumbnailResource, id: number, capture: ()=>Promise<PreviewImage>): Promise<string | undefined> {
  const key=this.key(resource,id)
  const existing=this.pending.get(key)
  if(existing) return existing
  const epoch=this.epochs.get(key)??0
  const task=this.generateOnce(resource,id,capture,epoch).finally(()=>this.pending.delete(key))
  this.pending.set(key,task)
  return task
 }
 private async generateOnce(resource: ThumbnailResource,id: number,capture: ()=>Promise<PreviewImage>,epoch: number) {
  const view=this.view(resource,id)
  try {
   const policy=await this.load(resource,id)
   if((this.epochs.get(this.key(resource,id))??0)!==epoch) return undefined
   if(policy.source==='user' || (policy.thumbnail && policy.generatedRevision===policy.revision)) return undefined
   view.status='generating'
   const image=await capture()
   if((this.epochs.get(this.key(resource,id))??0)!==epoch) return undefined
   return await this.persist(resource,id,policy,'automatic',image,epoch)
  } catch(error) {
   if(error instanceof HttpErrorResponse && error.status===409) {
    await this.load(resource,id).catch(()=>undefined)
    view.status='idle'
   } else view.status='error'
   return undefined
  } finally {
   if(view.status==='generating') view.status='idle'
  }
 }
 private async persist(resource: ThumbnailResource,id: number,policy: ThumbnailPolicy,source: 'user'|'automatic',image: PreviewImage,epoch: number) {
  const purpose=resource==='model'?'upload-model-thumbnail':'upload-project-thumbnail'
  const uploaded=await firstValueFrom(this.aws.uploadAndRegister(image.file,purpose,'thumbnail'))
  const isCurrent=()=> (this.epochs.get(this.key(resource,id))??0)===epoch
  if(!isCurrent()) return undefined
  await firstValueFrom(this.http.put(this.url(resource,id),{
   action:'save',source,revision:policy.revision,version:policy.version,thumbnail:'s3ref:'+uploaded.id,
  }))
  if((this.epochs.get(this.key(resource,id))??0)!==epoch) return undefined
  const view=this.view(resource,id)
  view.preview=image.preview
  view.policy={...policy,source,thumbnail:image.preview,generatedRevision:policy.revision,generatedAt:new Date().toISOString(),version:policy.version+1}
  view.status='idle'
  return image.preview
 }
}
